import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, Phone, MapPin, Facebook, Linkedin, Instagram, Youtube,
  ChevronUp, Send, Award, Building2, Sparkles, ExternalLink,
  MessageCircle, PhoneCall
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const Footer = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [email, setEmail] = useState('');

  // Show/hide back to top button based on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Newsletter subscription logic
    alert('Thank you for subscribing!');
    setEmail('');
  };

  // Quick Links
  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Courses', path: '/courses' },
    { name: 'Job Portal', path: '/jobs' },
    { name: 'Online Exam', path: '/assessment' },
    { name: 'Certificate Verification', path: '/certificate-verification' },
    { name: 'Placements', path: '/placements' }
  ];

  // Our Programs
  const ourPrograms = [
    { name: 'PMKVY 1.0 / 2.0 / 3.0 / 4.0', path: '/programs' },
    { name: 'NULM Programs', path: '/programs' },
    { name: 'ESDM Programs', path: '/programs' },
    { name: 'NAPS Apprenticeship', path: '/programs' },
    { name: 'Become a Partner', path: '/franchise' }
  ];

  // Social Links
  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/iqraskills', color: 'hover:bg-blue-600' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/iqraskills', color: 'hover:bg-blue-700' },
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/iqraskills', color: 'hover:bg-pink-600' },
    { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/@iqraskills', color: 'hover:bg-red-600' }
  ];

  // Trust Partners - All Government Affiliations
  const trustPartners = [
    { name: 'Skill India' },
    { name: 'NSDC' },
    { name: 'MSDE (NAPS)' },
    { name: 'ESSCI' },
    { name: 'TSSC' },
    { name: 'MSME' },
    { name: 'Startup India' }
  ];

  return (
    <>
      <footer 
        className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white overflow-hidden" 
        data-testid="main-footer"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        {/* Main Footer Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
            
            {/* Column 1: Logo & About */}
            <div className="lg:col-span-2">
              <Link to="/" className="inline-block mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_skill-india-partner/artifacts/h002c2co_1%20copy.png" 
                  alt="Iqra Skills Logo" 
                  className="h-14 w-auto"
                />
              </Link>
              <p className="text-xl font-semibold text-blue-300 mb-2">
                Empowering Skills, Building Futures
              </p>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                Leading skill development organization since 2011. Transforming lives through quality education and industry-relevant training.
              </p>
              
              {/* Social Icons */}
              <div className="flex gap-3 mb-8">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center transition-all duration-300 ${social.color} hover:scale-110 hover:shadow-lg hover:shadow-blue-500/20`}
                    aria-label={social.name}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>

              {/* Newsletter */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4 text-blue-400" />
                  Subscribe for Updates
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 text-sm"
                    required
                  />
                  <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-500 px-4">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-5 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-blue-400" />
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path} 
                      className="text-slate-400 hover:text-blue-400 text-sm transition-all duration-300 hover:translate-x-1 inline-flex items-center gap-1 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500/50 group-hover:bg-blue-400 transition-colors" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Our Programs */}
            <div>
              <h4 className="text-lg font-semibold mb-5 flex items-center gap-2">
                <Award className="w-4 h-4 text-blue-400" />
                Our Programs
              </h4>
              <ul className="space-y-3">
                {ourPrograms.map((program) => (
                  <li key={program.name}>
                    <Link 
                      to={program.path} 
                      className="text-slate-400 hover:text-blue-400 text-sm transition-all duration-300 hover:translate-x-1 inline-flex items-center gap-1 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/50 group-hover:bg-emerald-400 transition-colors" />
                      {program.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-5 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-400" />
                Contact Us
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 group">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <MapPin className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-slate-400 text-sm leading-relaxed">
                    Near UCO Bank, Bhartiya Link Road,<br />CHURU (RAJ) 331001
                  </span>
                </li>
                <li className="flex items-center gap-3 group">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <Phone className="w-4 h-4 text-blue-400" />
                  </div>
                  <a href="tel:+919521484003" className="text-slate-400 hover:text-blue-400 text-sm transition-colors">
                    +91 9521484003
                  </a>
                </li>
                <li className="flex items-center gap-3 group">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <Mail className="w-4 h-4 text-blue-400" />
                  </div>
                  <a href="mailto:info@iqraskills.org" className="text-slate-400 hover:text-blue-400 text-sm transition-colors">
                    info@iqraskills.org
                  </a>
                </li>
              </ul>

              {/* Call & WhatsApp Buttons */}
              <div className="mt-6 flex flex-col gap-2">
                <a href="tel:+919521484003">
                  <Button className="w-full bg-blue-600 hover:bg-blue-500 text-sm gap-2 group">
                    <PhoneCall className="w-4 h-4 group-hover:animate-pulse" />
                    Call Now
                  </Button>
                </a>
                <a href="https://wa.me/919521484003?text=Hi, I want to know about your courses" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-sm gap-2 group">
                    <MessageCircle className="w-4 h-4 group-hover:animate-pulse" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Trust Section */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="text-center mb-6">
              <h4 className="text-lg font-semibold mb-2 flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                Trusted & Affiliated With
              </h4>
              <p className="text-slate-400 text-sm">Recognized by Government of India initiatives</p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-4">
              {trustPartners.map((partner) => (
                <div 
                  key={partner.name} 
                  className="bg-white/10 backdrop-blur rounded-xl px-5 py-3 hover:bg-white/20 transition-all duration-300 hover:scale-105"
                >
                  <span className="text-white font-semibold text-sm">{partner.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="relative border-t border-white/10 bg-black/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm">
                © {new Date().getFullYear()} Iqra Skills Pvt Ltd. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <Link to="/privacy" className="text-slate-400 hover:text-blue-400 text-sm transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-slate-600">|</span>
                <Link to="/terms" className="text-slate-400 hover:text-blue-400 text-sm transition-colors">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-6 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/30 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl hover:shadow-blue-500/40 ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Back to top"
        data-testid="back-to-top-btn"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </>
  );
};

export default Footer;
